import React, { useCallback, useEffect, useState } from "react";
import { getCiCompetitorSettings } from "../../api/competitiveIntelligence";
import { Skeleton } from "../shared/Skeleton";
import { ErrorState } from "../shared/StateMessage";

// CompetitorSettingsPanel — slide-out showing the client's plan limits and
// upgrade options (spec §5.3, to-do B5 §7).
//
// Data comes from `ci_get_competitor_settings`, a READ RPC that returns:
//   {
//     plan: { tier, max_competitors, auto_radius_miles, discovery_radius_miles, ... },
//     upgrade_options: [{ tier, max_competitors, ... }, ...],
//     competitors: [...],          // active roster
//     recent_changes: [...]         // last 10 add/remove events
//   }
//
// We render plan limits (Standard / Plus / Enterprise) and, when the plan
// allows it, the upgrade options as upsell cards. This panel is read-only —
// the actual add/remove happens in the picker. Per B5 §6, when
// `config.client_can_manage` is false the picker hides its controls; this
// panel still renders so the client can see their limits and upgrade path.
//
// Error handling follows C4: a Postgres-level error rejects via ciRpc and we
// show a generic "Couldn't load" message — never the raw error text.

const fmtMiles = (value) => (value == null ? "—" : `${Number(value)} mi`);

// Plan tier display labels. The backend sends a code (e.g. "standard",
// "plus", "enterprise"); we Title-Case it so an unknown tier still renders.
const TIER_LABELS = {
  standard: "Standard",
  plus: "Plus",
  enterprise: "Enterprise",
};
const tierLabel = (tier) => (tier ? (TIER_LABELS[String(tier).toLowerCase()] ?? String(tier).charAt(0).toUpperCase() + String(tier).slice(1)) : "—");

const PlanCard = ({ plan, isCurrent, onSelect }) => {
  const tier = tierLabel(plan?.plan || plan?.tier);
  const maxCompetitors = plan?.max_competitors ?? plan?.competitor_limit;
  const autoRadius = plan?.auto_radius_miles ?? plan?.auto_radius;
  const discoveryRadius = plan?.discovery_radius_miles ?? plan?.discovery_radius;
  return (
    <div
      className={`rounded-xl border p-4 flex flex-col gap-2 ${
        isCurrent
          ? "border-[#32B1FF] bg-[#32B1FF]/5"
          : "border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{tier}</h3>
        {isCurrent && (
          <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#32B1FF]/10 text-[#32B1FF]">
            Current plan
          </span>
        )}
      </div>
      <dl className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
        {maxCompetitors != null && (
          <div className="flex justify-between gap-2">
            <dt className="text-gray-500 dark:text-gray-400">Competitors</dt>
            <dd className="font-medium tabular-nums">{maxCompetitors}</dd>
          </div>
        )}
        {autoRadius != null && (
          <div className="flex justify-between gap-2">
            <dt className="text-gray-500 dark:text-gray-400">Auto radius</dt>
            <dd className="font-medium tabular-nums">{fmtMiles(autoRadius)}</dd>
          </div>
        )}
        {discoveryRadius != null && (
          <div className="flex justify-between gap-2">
            <dt className="text-gray-500 dark:text-gray-400">Discovery radius</dt>
            <dd className="font-medium tabular-nums">{fmtMiles(discoveryRadius)}</dd>
          </div>
        )}
      </dl>
      {!isCurrent && onSelect && (
        <button
          type="button"
          onClick={onSelect}
          className="mt-1 self-start px-3 py-1.5 text-xs font-medium rounded-lg border border-[#32B1FF] bg-[#32B1FF]/10 text-[#32B1FF] hover:bg-[#32B1FF]/20 transition-colors"
        >
          Upgrade to {tier}
        </button>
      )}
    </div>
  );
};

const fmtChangeDate = (iso) => {
  if (!iso) return null;
  const ymd = iso.slice(0, 10);
  const [year, month, day] = ymd.split("-");
  if (!year || !month || !day) return null;
  return `${month}/${day}/${year}`;
};

const RecentChanges = ({ changes }) => {
  if (!Array.isArray(changes) || changes.length === 0) return null;
  return (
    <div className="space-y-2">
      <h4 className="text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">
        Recent changes
      </h4>
      <ul className="divide-y divide-gray-100 dark:divide-gray-800 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
        {changes.slice(0, 10).map((change, idx) => {
          const action = change?.action || change?.change_type;
          const isAdd = String(action).toLowerCase().includes("add");
          const name = change?.competitor_name || change?.name || "—";
          const date = fmtChangeDate(change?.changed_at || change?.created_at);
          const reason = change?.reason;
          return (
            <li key={idx} className="flex items-start gap-2 px-3 py-2 text-xs">
              <span
                className={`mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                  isAdd
                    ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                }`}
                aria-hidden="true"
              >
                {isAdd ? "+" : "−"}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-gray-900 dark:text-gray-100 truncate">
                  <span className="font-medium">{name}</span>
                  <span className="text-gray-500 dark:text-gray-400"> · {isAdd ? "added" : "removed"}</span>
                </div>
                <div className="text-gray-400 dark:text-gray-500 mt-0.5">
                  {date || "—"}
                  {reason && <span className="ml-1.5">· {reason}</span>}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const CompetitorSettingsPanel = ({ open, onClose, clientId }) => {
  const [state, setState] = useState({ loading: false, error: "", data: null });

  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    setState({ loading: true, error: "", data: null });
    getCiCompetitorSettings(clientId)
      .then((resp) => {
        if (cancelled) return;
        // READ RPC — a Postgres error rejects via ciRpc and hits .catch (C4).
        setState({ loading: false, error: "", data: resp?.data ?? null });
      })
      .catch((err) => {
        if (cancelled) return;
        // C4: Postgres-level error — never show raw to users.
        setState({ loading: false, error: err?.message || "Couldn't load competitor settings.", data: null });
      });
    return () => { cancelled = true; };
  }, [open, clientId]);

  if (!open) return null;

  const plan = state.data?.plan || {};
  const upgradeOptions = Array.isArray(state.data?.upgrade_options) ? state.data.upgrade_options : [];
  const competitors = Array.isArray(state.data?.active_competitors) ? state.data.active_competitors : [];
  const recentChanges = Array.isArray(state.data?.recent_changes) ? state.data.recent_changes : [];
  const currentTier = plan?.current || plan?.tier;
  const activeCount = competitors.length;

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Competitor settings">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close competitor settings"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
        tabIndex={-1}
      />

      {/* Panel — bottom-sheet on phone, side drawer on tablet+ (house responsive shell) */}
      <div className="fixed inset-x-0 bottom-0 top-0 md:inset-y-0 md:right-0 md:left-auto w-full md:w-[560px] md:max-w-[calc(100vw-60px)] bg-white dark:bg-gray-900 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Competitor settings</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Your plan limits, upgrade options, and recent roster changes.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 md:px-5 py-4 space-y-5">
          {state.loading ? (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : state.error ? (
            <ErrorState variant="inset" padding="py-4" message={state.error} />
          ) : (
            <>
              {/* Current plan + active count */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">Current plan</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{tierLabel(currentTier)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">Active competitors</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                    {activeCount}
                    {plan?.max_competitors != null && (
                      <span className="text-gray-400 dark:text-gray-500"> / {plan.max_competitors}</span>
                    )}
                  </span>
                </div>
                {plan?.auto_radius_miles != null && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">Auto radius</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{fmtMiles(plan.auto_radius_miles)}</span>
                  </div>
                )}
                {plan?.discovery_radius_miles != null && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">Discovery radius</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{fmtMiles(plan.discovery_radius_miles)}</span>
                  </div>
                )}
              </div>

              {/* Upgrade options — upsell cards (B5 §6: limit_reached surfaces these) */}
              {upgradeOptions.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">
                    Upgrade options
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {upgradeOptions.map((opt, idx) => (
                      <PlanCard key={opt?.tier || idx} plan={opt} isCurrent={false} />
                    ))}
                  </div>
                </div>
              )}

              {/* Recent changes — last 10 add/remove events */}
              <RecentChanges changes={recentChanges} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompetitorSettingsPanel;
